import React from "react";

import ReactMarkdown from "react-markdown";

import { Container, Paper, Typography, Box } from "@mui/material";

import { componentStyles, gradients } from "../styles/themeStyles";

const terms = `## 1. Introduction

Welcome to **Microcumulus**!

These Terms of Service ("Terms") govern your use of Microcumulus, our software suite powered by open source technologies (collectively, the "Service").

Please read these Terms carefully. Your use of the Service indicates that you agree to be bound by these Terms.

## 2. Privacy

We value your privacy. Please refer to our [Privacy Policy](/privacy) for information on how we collect, use and disclose your information.

## 3. Eligibility

You must be at least 18 years of age to use our Service. By using our Service, you represent and warrant that you are eligible.

## 4. Changes to the Terms

We reserve the right to change these Terms at any time, at our discretion. Your continued use of the Service following the posting of new Terms means that you accept those changes.

## 5. Accounts

To use our Service, you may need to create an account. You are responsible for the information you provide and maintaining the confidentiality of your account information.

You agree to:
- Provide accurate and complete information when creating your account
- Update your information to keep it accurate and current
- Keep your password secure and confidential
- Notify us immediately of any unauthorized access to your account
- Be solely responsible for all activities that occur under your account

## 6. Prohibited Activities

You may not engage in any activities that interfere with the Service, including, but not limited to:
- Distribution of malware, viruses, or harmful code
- Unauthorized access attempts or security breaches
- Scanning, probing, or testing the vulnerability of our systems
- Spamming or sending unsolicited communications
- Excessive or abusive usage that impacts performance for other users
- Attempting to reverse engineer, decompile, or disassemble the Service
- Using the Service for any illegal purpose or in violation of any laws

## 7. Intellectual Property

All content, features, and functionality of the Service, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are owned by Microcumulus or its licensors and are protected by copyright, trademark, and other intellectual property laws.

You may not:
- Modify, copy, distribute, transmit, display, perform, reproduce, publish, license, create derivative works from, transfer, or sell any information obtained from the Service
- Remove any copyright, trademark, or other proprietary notices from the Service
- Use any Microcumulus trademarks or logos without our prior written consent

## 8. Data Protection

We implement reasonable security measures to protect your data. However, no method of transmission over the Internet or electronic storage is 100% secure. By using our Service, you:
- Acknowledge these inherent risks
- Agree to use the Service at your own risk
- Are responsible for configuring your systems and usage to maintain appropriate security and backup of your data

## 9. Service Availability and Maintenance

We strive to provide uninterrupted service but:
- Do not guarantee the Service will be available at all times
- May perform scheduled maintenance during which the Service may be unavailable
- Will make reasonable efforts to provide advance notice of scheduled maintenance
- Reserve the right to modify, suspend, or discontinue any part of the Service at any time

## 10. Subscription and Payment Terms

For paid services:
- You agree to pay all fees according to the prices and terms in effect when you subscribe
- Subscription fees are billed in advance on a recurring basis
- You authorize us to store your payment information and to automatically charge your payment method
- All payments are non-refundable unless otherwise specified
- We may change our prices with 30 days' advance notice

## 11. Termination

We reserve the right to suspend or terminate your access to the Service:
- For violation of these Terms
- For engaging in prohibited activities
- For non-payment of fees
- At our sole discretion for any reason

Upon termination:
- Your right to access and use the Service immediately ceases
- We may delete your account and all associated data
- You remain liable for all amounts due up to the date of termination

## 12. Disclaimers

The Service is provided "as is" and "as available," without any warranties of any kind, either express or implied, including but not limited to:
- Warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance
- That the Service will be uninterrupted, error-free, or completely secure
- Regarding the accuracy, reliability, or completeness of any content or information provided

## 13. Limitation of Liability

In no event will Microcumulus, its affiliates, or their licensors, service providers, employees, agents, officers, or directors be liable for:
- Any direct, indirect, special, incidental, consequential, or punitive damages
- Loss of profits, data, use, goodwill, or other intangible losses
- Damages resulting from service interruptions, computer viruses, or unauthorized access to your account
- Any claims based on warranty, contract, tort, or any other legal theory

Our total liability to you shall not exceed the amount you have paid to us in the 12 months preceding the event giving rise to the liability.

## 14. Indemnification

You agree to defend, indemnify, and hold harmless Microcumulus and its affiliates from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your violation of these Terms or your use of the Service.

## 15. Governing Laws

These Terms are governed by and construed in accordance with the laws of the jurisdiction from which our Service is provided, without regard to its conflict of law principles.

Any dispute arising from these Terms shall be resolved exclusively in the courts located in that jurisdiction.

## 16. Support

Standard support is available during business hours. 24/7 support is available at additional cost. Contact us for pricing details.

Support services include:
- Technical assistance with service functionality
- Documentation and guidance for service usage
- Issue resolution according to our support policies

Response times vary based on issue severity and support tier.

## 17. Force Majeure

We shall not be liable for any delay or failure to perform resulting from causes outside our reasonable control, including but not limited to acts of God, natural disasters, pandemic, war, terrorism, riots, civil unrest, government actions, labor disputes, or internet service provider failures.

## 18. Severability

If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.

## 19. Entire Agreement

These Terms constitute the entire agreement between you and Microcumulus regarding the Service and supersede all prior and contemporaneous agreements, proposals, or representations, written or oral.

## 20. Contact

If you have any questions about these Terms, please contact us at support@microcumulus.com.`;

export const TOS = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper elevation={0} sx={componentStyles.pageHeader}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            ...gradients.text.style,
          }}
        >
          Terms of Service
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please read these terms carefully before using our services.
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            "& h2": {
              fontSize: "1.5rem",
              fontWeight: 600,
              mb: 2,
              mt: 4,
              "&:first-of-type": {
                mt: 0,
              },
            },
            "& p": {
              mb: 2,
            },
            "& a": {
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            },
            "& strong": {
              fontWeight: 600,
            },
          }}
        >
          <ReactMarkdown>{terms}</ReactMarkdown>
        </Box>
      </Paper>
    </Container>
  );
};
